import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Markdown } from '../components/markdown/markdown';
import './CanvasChat.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';

// 文件上传常量
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
const MAX_FILE_COUNT = 5;
const ALLOWED_FILE_TYPES = [
  'text/plain', 'text/markdown', 'text/html',
  'application/pdf', 'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'
];
const ALLOWED_FILE_EXTENSIONS = [
  '.txt', '.md', '.mdx', '.markdown', '.pdf', '.html',
  '.xlsx', '.xls', '.docx', '.csv',
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'
];

// 定义上传文件接口
interface UploadedFile {
  temp_file_id: string;
  minio_path: string;
  file_name: string;
  file_type: string;
}

// 定义消息类型接口
interface Message {
  type: 'user' | 'log';  // 用户消息或思考过程
  content: string;
  timestamp?: string;
  files?: UploadedFile[]; // 添加文件数组字段
}

// 定义API响应数据接口
interface ApiResponse {
  type: 'log' | 'result';  // log为思考过程，result为最终结果
  timestamp?: string;
  content: string;
}

/**
 * 发布应用运行组件（备用版本） - 专门处理 /apps/alternative/:publishUuid 路径
 * 提供公开访问的应用运行界面的备用版本，无需登录
 * 这是 RunApp 组件的副本，可以用于A/B测试或备用访问路径
 */
const RunAppAlternative: React.FC = () => {
  const { t } = useTranslation();
  const { publishUuid } = useParams<{ publishUuid: string }>();
  
  // 聊天相关状态
  const [inputText, setInputText] = useState<string>(''); // 输入文本
  const [isLoading, setIsLoading] = useState<boolean>(false); // 加载状态
  const [error, setError] = useState<string | null>(null); // 错误信息
  const [messages, setMessages] = useState<Message[]>([]);
  
  // 编辑器相关状态
  const [editorContent, setEditorContent] = useState<string>(''); // 编辑器内容（最终结果）
  const [documentTitle, setDocumentTitle] = useState<string>(t('apps.detail.untitledDoc', '未命名文档')); // 文档标题
  
  // 应用名称状态
  const [appName, setAppName] = useState<string>('');
  
  const messagesEndRef = useRef<HTMLDivElement>(null); // 用于聊天自动滚动

  // 文件上传相关状态
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * 处理聊天框内容滚动到底部
   */
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  /**
   * 获取发布应用的名称，并设置页面标题
   */
  useEffect(() => {
    if (publishUuid) {
      const fetchAppName = async () => {
        try {
          const response = await fetch(`https://ceshi.lightark.cc/api/v1/apps/publish/${publishUuid}/name`);
          if (response.ok) {
            const data = await response.json();
            setAppName(data.app_name);
            // 更新文档标题
            setDocumentTitle(data.app_name);
            // 更新页面标题（备用版本标识）
            document.title = `${data.app_name} - Alternative`;
          }
        } catch (error) {
          console.error('获取应用名称失败:', error);
        }
      };
      
      fetchAppName();
    }
  }, [publishUuid]);
  
  /**
   * 添加和移除body类名以控制滚动条样式
   */
  useEffect(() => {
    // 添加类名控制滚动条
    document.body.classList.add('canvas-chat-page');
    
    // 组件卸载时移除类名
    return () => {
      document.body.classList.remove('canvas-chat-page');
    };
  }, []);

  /**
   * 处理文件上传按钮点击
   */
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * 处理文件上传事件
   * @param event 文件输入事件
   */
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    await uploadFile(files[0]);
    // 清空输入以确保同一文件可以再次选择
    if (event.target) {
      event.target.value = '';
    }
  };

  /**
   * 上传单个文件到服务器
   * @param file 要上传的文件
   */
  const uploadFile = async (file: File) => {
    // 检查文件数量限制
    if (uploadedFiles.length >= MAX_FILE_COUNT) {
      setUploadError(t('apps.detail.maxFiles', `最多只能上传${MAX_FILE_COUNT}个文件`));
      return;
    }

    // 获取文件扩展名
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    // 验证文件类型和扩展名
    if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      setUploadError(t('apps.detail.unsupportedFileType', '不支持的文件类型。请上传以下格式的文件：txt、markdown、md、mdx、pdf、html、xlsx、xls、docx、csv、jpg、jpeg、png、gif、webp、svg'));
      return;
    }

    // 验证文件大小
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(t('apps.detail.fileSizeLimit', '文件大小不能超过15MB'));
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post<UploadedFile>(
        'https://ceshi.lightark.cc/api/v1/files/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }
      );

      if (response.data && response.data.temp_file_id) {
        setUploadedFiles(prev => [...prev, response.data]);
        setUploadError('');
      } else {
        setUploadError(t('apps.detail.uploadFailed', '文件上传失败'));
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      setUploadError(t('apps.detail.uploadFailed', '文件上传失败'));
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * 删除已上传的文件
   * @param tempFileId 要删除的文件临时ID
   */
  const handleRemoveFile = (tempFileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.temp_file_id !== tempFileId));
  };

  /**
   * 根据文件类型返回对应的图标
   * @param fileType 文件MIME类型
   * @returns 文件图标字符
   */
  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('word')) return '📝';
    return '📎';
  };

  /**
   * 渲染已上传的文件列表
   */
  const renderUploadedFiles = () => {
    if (uploadedFiles.length === 0) return null;

    return (
      <div className="uploaded-files">
        {uploadedFiles.map(file => (
          <div key={file.temp_file_id} className="uploaded-file">
            <span className="file-icon">{getFileIcon(file.file_type)}</span>
            <span className="file-name">{file.file_name}</span>
            <button
              type="button"
              className="remove-file"
              onClick={() => handleRemoveFile(file.temp_file_id)}
              aria-label={t('apps.detail.removeFile', '删除文件')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  /**
   * 处理表单提交 - 发送用户消息
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading || (!inputText.trim() && uploadedFiles.length === 0)) return;
    
    const currentInput = inputText.trim();
    const currentFiles = [...uploadedFiles];
    
    // 添加用户消息到聊天记录
    const userMessage: Message = {
      type: 'user',
      content: currentInput,
      timestamp: new Date().toLocaleTimeString(),
      files: currentFiles.length > 0 ? currentFiles : undefined
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setUploadedFiles([]);
    setIsLoading(true);
    setError(null);
    
    const timestamp = new Date().toISOString();
    
    try {
      await processApiResponse(currentInput, timestamp);
    } catch (error) {
      console.error('处理API响应失败:', error);
      setError(t('apps.detail.processFailed', '处理失败，请重试'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 处理模拟响应（开发测试用）
   * @param timestamp 时间戳
   */
  const processMockResponse = async (timestamp: string) => {
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    
    // 模拟思考过程
    const thinkingSteps = [
      '正在分析您的问题...',
      '正在搜索相关信息...',
      '正在生成回答...'
    ];
    
    for (const step of thinkingSteps) {
      const logMessage: Message = {
        type: 'log',
        content: step,
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, logMessage]);
      await delay(1000);
    }
    
    // 最终结果
    const resultContent = `# 回答结果 (备用版本)

这是一个模拟的回答内容。在实际应用中，这里会显示AI生成的具体回答。

## 要点总结
- 第一个要点
- 第二个要点  
- 第三个要点

\`\`\`javascript
// 示例代码
console.log('Hello World - Alternative Version');
\`\`\``;
    
    setEditorContent(resultContent);
  };

  /**
   * 处理API响应 - 与后端服务通信
   * @param inputText 用户输入文本
   * @param timestamp 时间戳
   */
  const processApiResponse = async (inputText: string, timestamp: string) => {
    try {
      const token = localStorage.getItem('token');
      
      // 准备请求数据
      const requestData = {
        inputs: {
          query: inputText,
          files: uploadedFiles.map(file => ({
            temp_file_id: file.temp_file_id,
            minio_path: file.minio_path,
            file_name: file.file_name,
            file_type: file.file_type
          }))
        },
        query: inputText,
        conversation_id: '',
        publish_uuid: publishUuid
      };

      console.log('发送请求数据:', requestData);

      const response = await fetch('https://ceshi.lightark.cc/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      let buffer = '';
      let finalResult = '';

      // 处理流式响应
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '') continue;
          
          // 处理 SSE 格式
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            
            if (data === '[DONE]') {
              console.log('Stream completed');
              continue;
            }

            try {
              const parsed: ApiResponse = JSON.parse(data);
              console.log('解析的数据:', parsed);

              if (parsed.type === 'log') {
                // 思考过程
                const logMessage: Message = {
                  type: 'log',
                  content: parsed.content,
                  timestamp: parsed.timestamp || new Date().toLocaleTimeString()
                };
                setMessages(prev => [...prev, logMessage]);
              } else if (parsed.type === 'result') {
                // 最终结果
                finalResult += parsed.content;
                setEditorContent(finalResult);
              }
            } catch (parseError) {
              console.error('解析JSON失败:', parseError, 'Raw data:', data);
            }
          }
        }
      }

      // 如果没有接收到结果，使用模拟响应
      if (!finalResult) {
        console.log('未接收到结果，使用模拟响应');
        await processMockResponse(timestamp);
      }

    } catch (error) {
      console.error('API请求失败:', error);
      
      // 发生错误时使用模拟响应
      console.log('API失败，使用模拟响应');
      await processMockResponse(timestamp);
    }
  };

  // 发送按钮内容
  const sendButtonContent = isLoading ? 
    <span className="loading-dots">●●●</span> : 
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.01 6.03L11.52 9.25L4 8.25L4.01 6.03ZM11.51 14.75L4 17.97V15.75L11.51 14.75ZM2.01 3L2 10L17 12L2 14L2.01 21L23 12L2.01 3Z" fill="currentColor"/>
    </svg>;

  // 聊天输入组件
  const chatInputWithUpload = (
    <form className="chat-input" onSubmit={handleSubmit}>
      {uploadError && <div className="upload-error">{uploadError}</div>}
      {renderUploadedFiles()}
      <div className="input-container">
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={t('apps.detail.run.modal.taskInputPlaceholder', '输入问题或上传文件')}
          disabled={isLoading}
        />
        <div className="input-actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            accept={ALLOWED_FILE_EXTENSIONS.join(',')}
            disabled={isLoading || uploadedFiles.length >= MAX_FILE_COUNT}
            aria-label={t('apps.detail.uploadFile', '上传文件')}
          />
          <button
            type="button"
            className="file-button"
            onClick={handleFileButtonClick}
            disabled={isLoading || uploadedFiles.length >= MAX_FILE_COUNT}
            aria-label={t('apps.detail.uploadFile', '上传文件')}
          >
            📎
          </button>
          <button 
            type="submit" 
            className="send-button" 
            disabled={isLoading || (!inputText.trim() && uploadedFiles.length === 0)}
            aria-label={t('apps.detail.send', '发送')}
          >
            {sendButtonContent}
          </button>
        </div>
      </div>
    </form>
  );

  return (
    <Layout hideFooter={true} hideHeader={true}>
      <div className="gemini-container run-mode">
        {/* 左侧聊天区域 - 显示思考过程 */}
        <div className="chat-panel">
          <div className="gemini-header">
            <div className="model-selector">
              <span className="gemini-logo">{appName ? `${appName} (备用)` : 'Agent (备用)'}</span>
              <div className="model-dropdown">
                <LanguageSelector />
              </div>
            </div>
          </div>
          
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div key={index} className={`chat-message ${message.type}`}>
                {message.type === 'log' && (
                  <div className="message-title">
                    <span className="document-title">{t('apps.detail.thinking', '思考过程')}</span>
                    {message.timestamp && <span className="message-time">{message.timestamp}</span>}
                  </div>
                )}
                <div className="message-content">
                  {message.type === 'log' ? (
                    <Markdown content={message.content} className="markdown-body chat-markdown" />
                  ) : (
                    <>
                      {message.content}
                      {message.files && message.files.length > 0 && (
                        <div className="message-files">
                          {message.files.map(file => (
                            <div key={file.temp_file_id} className="message-file">
                              <span className="file-icon">{getFileIcon(file.file_type)}</span>
                              <span className="file-name">{file.file_name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message log">
                <div className="message-title">
                  <span className="document-title">{t('apps.detail.thinkingNow', '思考中...')}</span>
                </div>
                <div className="message-content loading-message">
                  <div className="thinking-indicator">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="chat-input-container">
            {chatInputWithUpload}
          </div>
        </div>
        
        {/* 右侧编辑器面板 - 显示最终结果 */}
        <div className="editor-panel">
          <div className="editor-toolbar">
            <div className="document-actions">
              <button className="document-action" aria-label={t('apps.detail.createDoc', '创建文档')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" fill="currentColor"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <div className="editor-content">
            {editorContent ? (
              <Markdown content={editorContent} className="markdown-body" />
            ) : (
              <div className="empty-editor-message">{t('apps.detail.resultWillShowHere', '最终结果将显示在这里...')}</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RunAppAlternative; 